import { queryString } from '@helpers/QueryString';
import { throttle } from 'lodash';
import { getApiWebinarapi } from '@_api';

const queryFilterByLv1Key = 'webinar-filter-by-lv1';
const queryFilterByLv2Key = 'webinar-filter-by-lv2';
const queryFilterByCountryKey = 'webinar-filter-by-country';
const queryFilterByLanguageKey = 'webinar-filter-by-language';
const queryFilterByTypeKey = 'webinar-filter-by-type';
const queryFilterByCertificateKey = 'webinar-filter-by-certificate';

window.arjoWebinarCollectionModule =
  window.arjoWebinarCollectionModule ||
  (() => {
    const state = {
      isInitialized: false,
      elements: {},
    };

    const classNamePrefix = 'arjo-o-webinar-collection';

    const setUp = () => {
      const elements = [...document.querySelectorAll(`.${classNamePrefix}`)];
      elements.forEach(async (element) => {
        let isLoading = false;

        let hasMoreData = window.hasMoreWebinar !== undefined ? window.hasMoreWebinar : true;

        const dropdowns = element.querySelectorAll('.arjo-m-dropdown');

        const filterLv1Id = element.dataset.filterLv1Id;
        const filterLv2Id = element.dataset.filterLv2Id;
        const listId = element.dataset.listId;
        const blockId = element.dataset.blockId;

        if (!filterLv1Id || !filterLv2Id || !listId || !blockId) return;

        const queryFilterByLv1 = queryString.getQuery(queryFilterByLv1Key) || null;
        const queryFilterByLv2 = queryString.getQuery(queryFilterByLv2Key) || null;
        const queryFilterByCountry = queryString.getQuery(queryFilterByCountryKey) || null;
        const queryFilterByLanguage = queryString.getQuery(queryFilterByLanguageKey) || null;
        const queryFilterByType = queryString.getQuery(queryFilterByTypeKey) || null;
        const queryFilterByCertificate = queryString.getQuery(queryFilterByCertificateKey) || null;

        const listBlock = window.arjoWebinarListModule.getElement(listId);
        const filterLv1Block = window.arjoFilterProductListModule.getElement(filterLv1Id);
        const filterLv2Block = window.arjoFilterProductListModule.getElement(filterLv2Id);

        const scrollToWebinarList = () => {
          if (
            queryFilterByLv1 ||
            queryFilterByLv2 ||
            queryFilterByCountry ||
            queryFilterByLanguage ||
            queryFilterByType ||
            queryFilterByCertificate
          ) {
            element.scrollIntoViewEx();
          }
        };

        const renderDropdown = (filters) => {
          filters?.forEach((filter) => {
            const filterElement = [...dropdowns].find((dropdown) => dropdown.dataset.dropdownId === filter.dropdownId);

            if (!filterElement) return;
            const optionInputs = filterElement.querySelectorAll('.arjo-m-dropdown__options-input');

            [...optionInputs].forEach((input) => {
              const label = filterElement.querySelector(`.arjo-m-dropdown__options-label[for="${input.id}"]`);

              const option = filter?.options.find((option) => option?.value === input.value && !option.hidden);

              if (!option && input?.value) {
                label.hidden = true;
                return;
              }

              label.removeAttribute('hidden');

              const currentLabelSum = label.querySelector(`.sum`);

              if (!currentLabelSum) return;

              currentLabelSum.textContent = ` (${option?.count ?? 0}/${option?.total ?? 0})`;
            });
          });
        };

        const getWebinars = async (skipDefault) => {
          try {
            isLoading = true;
            element.classList.add(`${classNamePrefix}--show-spinner`);
            const queryStringObject = queryString.getQueryStringAsObject();
            const listData = listBlock.currentState?.data;
            const skip = skipDefault === 0 ? skipDefault : listData?.length;

            const response = await getApiWebinarapi({
              'webinar-filter-by-certificate': queryStringObject['webinar-filter-by-certificate'],
              'webinar-filter-by-country': queryStringObject['webinar-filter-by-country'],
              'webinar-filter-by-lv1': queryStringObject['webinar-filter-by-lv1'],
              'webinar-filter-by-lv2': queryStringObject['webinar-filter-by-lv2'],
              'webinar-filter-by-language': queryStringObject['webinar-filter-by-language'],
              'webinar-filter-by-type': queryStringObject['webinar-filter-by-type'],
              skip,
            });

            if (!response || !response.success) return;

            const data = response.data;
            const concatenationData = skipDefault !== undefined ? data?.webinars : [...listData, ...data?.webinars];
            listBlock.setData(concatenationData);
            listBlock.setState(listBlock.currentState);
            hasMoreData = data?.hasMore;
            if (hasMoreData) {
              handleInfiniteScroll();
            }
            renderDropdown(data?.filterGroups);
          } catch (error) {
            console.log(error);
          } finally {
            isLoading = false;
            element.classList.remove(`${classNamePrefix}--show-spinner`);
          }
        };

        const getWebinarOnPushingState = async ({ data, unused, url, init }) => {
          if (!init) {
            await getWebinars(0);
            window.history.pushState(data, unused, url);
          }
        };

        const getFilterDefaultOption = (options, selectedValue) => {
          return options.map((option) => {
            if (option.value === selectedValue?.value) {
              return { ...option, isDefault: true };
            }

            return option;
          });
        };

        const initFilterLv1 = (options) => {
          const selectedValue = options.find((opt) => opt.value === queryFilterByLv1 ?? opt.isDefault);

          const defaultOptions = getFilterDefaultOption(options, selectedValue);

          filterLv1Block.setState({
            expanded: false,
            currentValue: selectedValue && selectedValue?.value,
            options: defaultOptions,
            init: true,
          });
        };

        const initFilterLv2 = (options) => {
          const selectedValue = options.find((opt) => opt.value === queryFilterByLv2 ?? opt.isDefault);

          const defaultOptions = getFilterDefaultOption(options, selectedValue);

          filterLv2Block.setState({
            expanded: false,
            currentValue: selectedValue && selectedValue?.value,
            options: defaultOptions,
            init: true,
          });
        };

        const initDropdownData = () => {
          for (const dropdown of dropdowns) {
            const dropdownName = dropdown.dataset.name;
            if (dropdownName === 'country' && queryFilterByCountry) {
              dropdown.dispatchEvent(
                new CustomEvent('optionChange', {
                  detail: {
                    value: queryFilterByCountry,
                    init: true,
                  },
                })
              );
            } else if (dropdownName === 'language' && queryFilterByLanguage) {
              dropdown.dispatchEvent(
                new CustomEvent('optionChange', {
                  detail: {
                    value: queryFilterByLanguage,
                    init: true,
                  },
                })
              );
            } else if (dropdownName === 'type' && queryFilterByType) {
              dropdown.dispatchEvent(new CustomEvent('optionChange', { detail: { value: queryFilterByType, init: true } }));
            } else if (dropdownName === 'certificate' && queryFilterByCertificate) {
              dropdown.dispatchEvent(
                new CustomEvent('optionChange', {
                  detail: {
                    value: queryFilterByCertificate,
                    init: true,
                  },
                })
              );
            }
          }
        };

        const initData = (data) => {
          listBlock.setData(data);
          listBlock.setState({
            filterByLv1Category: filterLv1Block.currentValue,
            filterByLv2Category: filterLv2Block.currentValue,
            dropdowns: {
              countryLabel: queryFilterByCountry,
              languageLabel: queryFilterByLanguage,
              typeLabel: queryFilterByType,
              certificateLabel: queryFilterByCertificate,
              toString() {
                return `${this.countryLabel ?? null} | ${this.languageLabel ?? null} | ${this.typeLabel ?? null} | ${this.certificateLabel ?? null}`;
              },
            },
          });

          scrollToWebinarList();
          initDropdownData();
        };

        state.elements[blockId] = (() => {
          return {
            initData,
            initFilterLv1,
            initFilterLv2,
          };
        })();

        const lv1FilterHandler = async (e) => {
          const label = e.detail.filterForTracking;
          const FilterTracker = window.GA4?.analyticsGa4?.FilterTracker;

          if (FilterTracker && label && listBlock.currentState.filterByLv2Label) {
            FilterTracker.pushFilter(label, listBlock.currentState.filterByLv2Label, listBlock.currentState.dropdowns?.toString());
          }

          const value = e.detail.filterForValue;

          if (value) {
            await getWebinarOnPushingState({
              data: null,
              unused: '',
              url: queryString.getUrlQueryString(queryFilterByLv1Key, value, true).href,
              init: e.detail.init,
            });
          }

          listBlock.setState({
            filterByLv1Category: value,
            filterByLv1Label: label,
            filterByLv2Category: listBlock.currentState.filterByLv2Category,
            filterByLv2Label: listBlock.currentState.filterByLv2Label,
          });
        };

        const lv2FilterHandler = async (e) => {
          const label = e.detail.filterForTracking;
          const FilterTracker = window.GA4?.analyticsGa4?.FilterTracker;

          if (FilterTracker && label && listBlock.currentState.filterByLv1Label) {
            FilterTracker.pushFilter(listBlock.currentState.filterByLv1Label, label, listBlock.currentState.dropdowns?.toString());
          }

          const value = e.detail.filterForValue;

          if (value) {
            await getWebinarOnPushingState({
              data: null,
              unused: '',
              url: queryString.getUrlQueryString(queryFilterByLv2Key, value, true).href,
              init: e.detail.init,
            });
          }

          listBlock.setState({
            filterByLv1Category: listBlock.currentState.filterByLv1Category,
            filterByLv1Label: listBlock.currentState.filterByLv1Label,
            filterByLv2Category: value?.toLowerCase(),
            filterByLv2Label: label,
          });
        };

        window.removeEventListener(`filterProductList:${filterLv1Id}`, lv1FilterHandler);
        window.addEventListener(`filterProductList:${filterLv1Id}`, lv1FilterHandler);

        window.removeEventListener(`filterProductList:${filterLv2Id}`, lv2FilterHandler);
        window.addEventListener(`filterProductList:${filterLv2Id}`, lv2FilterHandler);

        // set label text
        const setLabelText = (label, text) => {
          label.textContent = text;
        };

        // dropdown handle change
        const handleDropdownChange = async (event) => {
          // default text
          const defaultCountryInput = element.querySelector('input[name="country"][value=""]');
          const defaultCountryText = defaultCountryInput?.dataset?.label;
          const defaultLanguageInput = element.querySelector('input[name="language"][value=""]');
          const defaultLanguageText = defaultLanguageInput?.dataset?.label;
          const defaultTypeInput = element.querySelector('input[name="type"][value=""]');
          const defaultTypeText = defaultTypeInput?.dataset?.label;
          const defaultCertificateInput = element.querySelector('input[name="certificate"][value=""]');
          const defaultCertificateText = defaultCertificateInput?.dataset?.label;

          const target = event.target;
          const allText = window?.webinarConfig?.allText || 'All';

          const inputElement = event.detail.inputElement;
          const trackingLabel = inputElement?.dataset?.trackingLabel ?? null;

          const value = event?.detail?.value || '';

          if (target?.dataset?.name === 'country') {
            const label = element?.querySelector(`label[for="${defaultCountryInput?.id}"]`);

            listBlock.setState({ dropdowns: { ...listBlock.currentState.dropdowns, countryLabel: trackingLabel } });

            await getWebinarOnPushingState({
              data: null,
              unused: '',
              url: queryString.getUrlQueryString(queryFilterByCountryKey, value, true).href,
              init: event.detail.init,
            });

            label && setLabelText(label, value ? allText : defaultCountryText);
          } else if (target?.dataset?.name === 'language') {
            const label = element?.querySelector(`label[for="${defaultLanguageInput?.id}"]`);

            listBlock.setState({ dropdowns: { ...listBlock.currentState.dropdowns, languageLabel: trackingLabel } });

            await getWebinarOnPushingState({
              data: null,
              unused: '',
              url: queryString.getUrlQueryString(queryFilterByLanguageKey, value, true).href,
              init: event.detail.init,
            });

            label && setLabelText(label, value ? allText : defaultLanguageText);
          } else if (target?.dataset?.name === 'type') {
            const label = element?.querySelector(`label[for="${defaultTypeInput?.id}"]`);

            await getWebinarOnPushingState({
              data: null,
              unused: '',
              url: queryString.getUrlQueryString(queryFilterByTypeKey, value, true).href,
              init: event.detail.init,
            });

            listBlock.setState({ dropdowns: { ...listBlock.currentState.dropdowns, typeLabel: trackingLabel } });

            label && setLabelText(label, value ? allText : defaultTypeText);
          } else if (target?.dataset?.name === 'certificate') {
            const label = element?.querySelector(`label[for="${defaultCertificateInput?.id}"]`);

            await getWebinarOnPushingState({
              data: null,
              unused: '',
              url: queryString.getUrlQueryString(queryFilterByCertificateKey, value, true).href,
              init: event.detail.init,
            });

            listBlock.setState({ dropdowns: { ...listBlock.currentState.dropdowns, certificateLabel: trackingLabel } });

            label && setLabelText(label, value ? allText : defaultCertificateText);
          }

          const FilterTracker = window.GA4?.analyticsGa4?.FilterTracker;

          FilterTracker?.pushFilter(
            listBlock.currentState.filterByLv1Label,
            listBlock.currentState.filterByLv2Label,
            listBlock.currentState.dropdowns?.toString()
          );
        };

        for (const dropdown of dropdowns) {
          dropdown.addEventListener('dropdownChange', handleDropdownChange);
        }

        const handleInfiniteScroll = () => {
          const loadMoreOnScroll = () => {
            if (!hasMoreData) {
              window.removeEventListener('scroll', loadMoreOnScroll);
              return;
            }

            const throttler = throttle(async () => {
              const cards = element.querySelectorAll(`.arjo-o-webinar-card`);

              if (!cards?.length || isLoading) return;

              const gridHeight = element.offsetTop + element.scrollHeight;
              const windowScrollPosition = window.scrollY + window.innerHeight;
              const threshold = 100;

              if (windowScrollPosition >= gridHeight - threshold) {
                // Trigger the function to load more content
                await getWebinars();
              }
            }, 1000);

            throttler();
          };

          window.addEventListener('scroll', loadMoreOnScroll);
        };

        handleInfiniteScroll();

        // Dispatch custom event to help outside script add their function
        window.dispatchEvent(new CustomEvent('webinarLoaded'));
      });
    };

    const init = () => {
      if (state.isInitialized) return;
      setUp();
      state.isInitialized = true;
    };

    const getElement = (id) => {
      return state.elements[id];
    };

    return { init, getElement };
  })();

window.addEventListener('load', window.arjoWebinarCollectionModule.init);
